
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Study } from '../types';

export const generateStudyPDF = async (study: Study) => {
    // Create a hidden container for PDF rendering
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '800px'; // Standard width for PDF rendering
    container.style.backgroundColor = 'white';
    container.style.padding = '40px';
    container.style.fontFamily = 'serif';
    container.style.color = '#111827'; // gray-900

    // Header
    const header = document.createElement('div');
    header.style.display = 'flex';
    header.style.justifyContent = 'space-between';
    header.style.alignItems = 'flex-start';
    header.style.marginBottom = '20px';
    header.style.borderBottom = '2px solid #E5E7EB';
    header.style.padding = '10px 0 20px 0'; // Added top/bottom padding to prevent clipping

    const logoGroup = document.createElement('div');
    logoGroup.style.display = 'flex';
    logoGroup.style.flexDirection = 'column';

    const logoText = document.createElement('div');
    logoText.innerHTML = `
    <div style="display: flex; align-items: center; gap: 8px;">
      <span style="font-weight: bold; font-size: 24px; color: #111827;">Bible Aware</span>
    </div>
  `;

    const urlText = document.createElement('div');
    urlText.innerText = 'www.BibleAware.com';
    urlText.style.fontSize = '12px';
    urlText.style.color = '#6B7280';
    urlText.style.marginTop = '4px';

    logoGroup.appendChild(logoText);
    logoGroup.appendChild(urlText);
    header.appendChild(logoGroup);
    container.appendChild(header);

    // Title
    const title = document.createElement('h1');
    title.innerText = study.title;
    title.style.fontSize = '32px';
    title.style.fontWeight = 'bold';
    title.style.margin = '20px 0';
    title.style.padding = '10px 0'; // Added padding to prevent bottom clipping
    title.style.textAlign = 'center';
    container.appendChild(title);

    // Description
    if (study.description) {
        const desc = document.createElement('p');
        desc.innerText = study.description;
        desc.style.fontSize = '16px';
        desc.style.lineHeight = '1.6';
        desc.style.color = '#4B5563';
        desc.style.marginBottom = '30px';
        desc.style.textAlign = 'center';
        desc.style.fontStyle = 'italic';
        container.appendChild(desc);
    }

    // Segments
    for (const segment of study.segments) {
        const segmentDiv = document.createElement('div');
        segmentDiv.style.marginBottom = '30px';
        segmentDiv.style.padding = '10px 0'; // Added padding for better capture
        segmentDiv.style.pageBreakInside = 'avoid';
        segmentDiv.setAttribute('data-segment-block', 'true'); // Helper for selector

        if (segment.type === 'text' || segment.type === 'image-text') {
            const text = document.createElement('p');
            text.innerText = segment.content;
            text.style.fontSize = '14px';
            text.style.lineHeight = '1.8';
            text.style.whiteSpace = 'pre-wrap';
            segmentDiv.appendChild(text);

            if (segment.type === 'image-text' && segment.metadata?.imageUrl) {
                const img = document.createElement('img');
                img.src = segment.metadata.imageUrl;
                img.style.width = '100%';
                img.style.borderRadius = '8px';
                img.style.marginTop = '15px';
                img.crossOrigin = 'anonymous'; // Important for html2canvas
                segmentDiv.appendChild(img);
            }
        } else if (segment.type === 'verse') {
            const verseBox = document.createElement('div');
            verseBox.style.backgroundColor = '#F3F4F6';
            verseBox.style.padding = '20px';
            verseBox.style.borderRadius = '8px';
            verseBox.style.borderLeft = '4px solid #1e3a8a';

            const verseText = document.createElement('p');
            verseText.innerText = `"${segment.content}"`;
            verseText.style.fontSize = '16px';
            verseText.style.fontStyle = 'italic';
            verseText.style.marginBottom = '10px';

            const reference = document.createElement('p');
            const ref = segment.metadata;
            reference.innerText = `— ${ref?.book} ${ref?.chapter}:${ref?.verses} ${ref?.version || ''}`;
            reference.style.fontSize = '14px';
            reference.style.fontWeight = 'bold';
            reference.style.textAlign = 'right';

            verseBox.appendChild(verseText);
            verseBox.appendChild(reference);
            segmentDiv.appendChild(verseBox);
        } else if (segment.type === 'image') {
            const img = document.createElement('img');
            img.src = segment.content;
            img.style.width = '100%';
            img.style.borderRadius = '8px';
            img.crossOrigin = 'anonymous';
            segmentDiv.appendChild(img);

            if (segment.metadata?.caption) {
                const caption = document.createElement('p');
                caption.innerText = segment.metadata.caption;
                caption.style.fontSize = '12px';
                caption.style.color = '#6B7280';
                caption.style.textAlign = 'center';
                caption.style.marginTop = '8px';
                segmentDiv.appendChild(caption);
            }
        }

        container.appendChild(segmentDiv);
    }

    document.body.appendChild(container);

    // Wait for images to load if any
    const images = container.getElementsByTagName('img');
    const imagePromises = Array.from(images).map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise(resolve => {
            img.onload = resolve;
            img.onerror = resolve; // Continue even if image fails
        });
    });

    await Promise.all(imagePromises);

    try {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = 210;
        const pageHeight = 297;
        const margin = 15; // 15mm margin
        const contentWidth = pageWidth - (margin * 2);

        let currentY = margin;

        // Helper to capture and add an element to PDF
        const addElementToPDF = async (element: HTMLElement) => {
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff'
            });

            const imgData = canvas.toDataURL('image/png');
            const elHeightMm = (canvas.height * contentWidth) / canvas.width;

            // Check if element fits on current page
            // If it exceeds the page height minus bottom margin, add a new page
            if (currentY + elHeightMm > pageHeight - margin) {
                pdf.addPage();
                currentY = margin;
            }

            pdf.addImage(imgData, 'PNG', margin, currentY, contentWidth, elHeightMm);
            currentY += elHeightMm + 5; // Add a 5mm gap between segments
        };

        // Add Header
        await addElementToPDF(header);

        // Add Title
        await addElementToPDF(title);

        // Add Description
        if (study.description) {
            const descEl = container.querySelector('p[style*="font-style: italic"]');
            if (descEl) await addElementToPDF(descEl as HTMLElement);
        }

        // Add Segments one by one
        const segmentDivs = container.querySelectorAll('[data-segment-block="true"]');
        for (const div of Array.from(segmentDivs)) {
            await addElementToPDF(div as HTMLElement);
        }

        // Add page numbers
        const pageCount = (pdf as any).internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            pdf.setPage(i);
            pdf.setFontSize(10);
            pdf.setTextColor(150);
            pdf.text(
                `Page ${i} of ${pageCount}`,
                pageWidth / 2,
                pageHeight - 7,
                { align: 'center' }
            );
        }

        pdf.save(`${study.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);
    } catch (error) {
        console.error('PDF Generation Error:', error);
        throw error;
    } finally {
        document.body.removeChild(container);
    }
};
